// ErrorBoundary.js — catches render errors and offers a way to recover.
//
// TARGET PATH: src/shared/components/ErrorBoundary.js
//
// Replicated from ecoh-ui-module's shared/components/ErrorBoundary.js, which is
// the reference for UI patterns across our front ends. The class shape, the
// props (`fallback`, `componentName`, `compact`), the retry behaviour and the
// `withErrorBoundary` HOC are all kept identical so the two repos read the same.
//
// Three deliberate deviations, each forced by this repo's dependencies:
//
//   1. Icons are inline SVG rather than lucide-react, which is not a dependency
//      here. Navbar and AlertDetailPage already draw their icons this way.
//   2. Styling lives in ErrorBoundary.css against the theme variables instead of
//      the reference's inline styles. The reference hardcodes light-mode colours
//      (#fef2f2 on #dc2626); this app has a dark theme, and hardcoding would
//      leave the error screen unreadable in it — which is exactly the failure
//      mode this component exists to prevent.
//   3. No `recordError` call: there is no RUM wiring in this repo yet. When one
//      arrives, it belongs in componentDidCatch alongside the console.error.
//
// Why a class and not a hook: catching render errors is only possible through
// getDerivedStateFromError / componentDidCatch. There is no hook equivalent.

import React, { Component } from 'react';
import './ErrorBoundary.css';

/* ── Icons ─────────────────────────────────────────────────── */
function AlertTriangleIcon({ size = 40 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function RefreshIcon({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 4v6h-6" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

/**
 * Wraps a subtree so a render error there shows a message instead of blanking
 * the screen.
 *
 * Without this, an uncaught render error unmounts everything React has
 * committed and the user is left staring at empty space with no indication that
 * anything failed — the alert detail page did exactly that in SIT.
 *
 * Usage:
 *   <ErrorBoundary componentName="Alerts">{children}</ErrorBoundary>
 *   <ErrorBoundary fallback={({ error, retry }) => <MyScreen ... />}>...
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // The console is the only telemetry available in this app today, and it is
    // what someone will be asked to paste when reporting a blank page.
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback({ error: this.state.error, retry: this.handleRetry })
          : this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          componentName={this.props.componentName}
          compact={this.props.compact}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * The default screen shown when no `fallback` is supplied.
 *
 * Chunk-load failures get their own wording. They are not bugs — they happen
 * when a deploy replaces the hashed bundles under a tab that is still open — and
 * telling that user to reload is the actual fix, where "something went wrong"
 * would send them to raise a defect instead.
 */
const ErrorFallback = ({ error, onRetry, componentName, compact = false }) => {
  const isChunkError =
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.name === 'ChunkLoadError';

  if (compact) {
    return (
      <div className="eb-compact" role="alert">
        <span className="eb-compact-icon"><AlertTriangleIcon size={18} /></span>
        <span className="eb-compact-text">
          {isChunkError ? 'Failed to load component' : 'Something went wrong'}
        </span>
        <button type="button" className="eb-btn eb-btn-primary eb-btn-sm" onClick={onRetry}>
          <RefreshIcon size={12} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="eb-page" role="alert">
      <span className="eb-icon"><AlertTriangleIcon size={40} /></span>

      <h3 className="eb-title">
        {isChunkError
          ? `Failed to load ${componentName || 'component'}`
          : 'Something went wrong'}
      </h3>

      <p className="eb-message">
        {isChunkError
          ? 'This might be due to a network issue or an app update. Please check your connection and try again.'
          : error?.message || 'An unexpected error occurred.'}
      </p>

      <button type="button" className="eb-btn eb-btn-primary" onClick={onRetry}>
        <RefreshIcon size={16} />
        Try Again
      </button>
    </div>
  );
};

/**
 * Wraps any component in a boundary.
 *
 *   export default withErrorBoundary(MyPage, { componentName: 'MyPage' });
 */
export const withErrorBoundary = (WrappedComponent, options = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary {...options}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorBoundary;
};

export { ErrorFallback };
export default ErrorBoundary;
